/**
 * Layout engine shared by the editor preview (canvas) and the renderer (ffmpeg filtergraph), so what
 * you see while editing is pixel-for-pixel what gets rendered.
 */

export const LAYOUT_IDS = ['solo', 'grid', 'spotlight', 'screen-side', 'screen-pip'] as const
export type LayoutId = (typeof LAYOUT_IDS)[number]

export type VisualKind = 'camera' | 'screen'
export type Fit = 'cover' | 'contain'

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

export interface Canvas {
  w: number
  h: number
}

export interface SlotInput {
  id: string
  kind: VisualKind
}

export interface Placement {
  sourceId: string
  rect: Rect
  fit: Fit
  z: number
}

export const LAYOUT_INFO: Record<LayoutId, { label: string; hint: string; maxSlots: number }> = {
  solo: { label: 'Solo', hint: 'One source, full frame', maxSlots: 1 },
  grid: { label: 'Grid', hint: 'Everyone in equal tiles', maxSlots: 9 },
  spotlight: { label: 'Spotlight', hint: 'First source large, the rest in a strip', maxSlots: 7 },
  'screen-side': { label: 'Screen + side', hint: 'First source large, cameras beside it', maxSlots: 6 },
  'screen-pip': { label: 'Picture in picture', hint: 'First source full frame, cameras on top', maxSlots: 5 },
}

const CAMERA_ASPECT = 16 / 9
const MIN_TILE_ASPECT = 0.75

/** yuv420p needs even sizes and offsets; rounding here keeps preview and render identical. */
const even = (v: number) => Math.max(2, Math.round(v / 2) * 2)
const evenPos = (v: number) => Math.round(v / 2) * 2

function rect(x: number, y: number, w: number, h: number): Rect {
  return { x: evenPos(x), y: evenPos(y), w: even(w), h: even(h) }
}

function inset(canvas: Canvas, by: number): Rect {
  return { x: by, y: by, w: canvas.w - by * 2, h: canvas.h - by * 2 }
}

export function layoutGap(canvas: Canvas): number {
  return Math.round(Math.min(canvas.w, canvas.h) * 0.02)
}

const fitFor = (kind: VisualKind): Fit => (kind === 'screen' ? 'contain' : 'cover')

/** Equal tiles, arranged to keep them as large and as close to 16:9 as the area allows. */
export function gridRects(n: number, area: Rect, gap: number): Rect[] {
  if (n <= 0) return []
  let best: { cols: number; rows: number; tw: number; th: number; area: number } | null = null
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols)
    let tw = (area.w - gap * (cols - 1)) / cols
    let th = (area.h - gap * (rows - 1)) / rows
    if (tw <= 0 || th <= 0) continue
    if (tw / th > CAMERA_ASPECT) tw = th * CAMERA_ASPECT
    if (tw / th < MIN_TILE_ASPECT) th = tw / MIN_TILE_ASPECT
    const used = tw * th * n
    if (!best || used > best.area + 1e-6) best = { cols, rows, tw, th, area: used }
  }
  if (!best) return []
  const { cols, rows, tw, th } = best
  const gridH = rows * th + (rows - 1) * gap
  const top = area.y + (area.h - gridH) / 2
  const rects: Rect[] = []
  for (let r = 0; r < rows; r++) {
    const inRow = Math.min(cols, n - r * cols)
    const rowW = inRow * tw + (inRow - 1) * gap
    const left = area.x + (area.w - rowW) / 2
    for (let c = 0; c < inRow; c++) {
      rects.push(rect(left + c * (tw + gap), top + r * (th + gap), tw, th))
    }
  }
  return rects
}

/** A horizontal row of 16:9 tiles, centred in `area`, shrunk until they fit. */
function stripRects(n: number, area: Rect, gap: number): Rect[] {
  if (n <= 0) return []
  let th = area.h
  let tw = th * CAMERA_ASPECT
  const total = n * tw + (n - 1) * gap
  if (total > area.w) {
    tw = (area.w - (n - 1) * gap) / n
    th = tw / CAMERA_ASPECT
  }
  const rowW = n * tw + (n - 1) * gap
  const left = area.x + (area.w - rowW) / 2
  const top = area.y + (area.h - th) / 2
  return Array.from({ length: n }, (_, i) => rect(left + i * (tw + gap), top, tw, th))
}

/** A vertical column of 16:9 tiles, centred in `area`. */
function columnRects(n: number, area: Rect, gap: number): Rect[] {
  if (n <= 0) return []
  let tw = area.w
  let th = tw / CAMERA_ASPECT
  if (n * th + (n - 1) * gap > area.h) {
    th = (area.h - (n - 1) * gap) / n
    tw = Math.min(area.w, th * CAMERA_ASPECT)
  }
  const colH = n * th + (n - 1) * gap
  const top = area.y + (area.h - colH) / 2
  const left = area.x + (area.w - tw) / 2
  return Array.from({ length: n }, (_, i) => rect(left, top + i * (th + gap), tw, th))
}

export function computeLayout(layout: LayoutId, slots: SlotInput[], canvas: Canvas): Placement[] {
  const used = slots.slice(0, LAYOUT_INFO[layout].maxSlots)
  if (used.length === 0) return []
  const gap = layoutGap(canvas)
  const landscape = canvas.w >= canvas.h
  const [primary, ...rest] = used as [SlotInput, ...SlotInput[]]
  const place = (s: SlotInput, r: Rect, z = 0): Placement => ({ sourceId: s.id, rect: r, fit: fitFor(s.kind), z })

  switch (layout) {
    case 'solo':
      return [place(primary, rect(0, 0, canvas.w, canvas.h))]

    case 'grid':
      return gridRects(used.length, inset(canvas, gap), gap).map((r, i) => place(used[i]!, r))

    case 'spotlight': {
      const area = inset(canvas, gap)
      if (rest.length === 0) return [place(primary, rect(area.x, area.y, area.w, area.h))]
      const stripH = area.h * (landscape ? 0.22 : 0.14)
      const mainH = area.h - stripH - gap
      const strip = stripRects(rest.length, { x: area.x, y: area.y + mainH + gap, w: area.w, h: stripH }, gap)
      return [place(primary, rect(area.x, area.y, area.w, mainH)), ...strip.map((r, i) => place(rest[i]!, r))]
    }

    case 'screen-side': {
      const area = inset(canvas, gap)
      if (rest.length === 0) return [place(primary, rect(area.x, area.y, area.w, area.h))]
      if (landscape) {
        const colW = area.w * 0.24
        const mainW = area.w - colW - gap
        const col = columnRects(rest.length, { x: area.x + mainW + gap, y: area.y, w: colW, h: area.h }, gap)
        return [place(primary, rect(area.x, area.y, mainW, area.h)), ...col.map((r, i) => place(rest[i]!, r))]
      }
      const mainH = area.h * 0.45
      const grid = gridRects(rest.length, { x: area.x, y: area.y + mainH + gap, w: area.w, h: area.h - mainH - gap }, gap)
      return [place(primary, rect(area.x, area.y, area.w, mainH)), ...grid.map((r, i) => place(rest[i]!, r))]
    }

    case 'screen-pip': {
      const main = place(primary, rect(0, 0, canvas.w, canvas.h))
      const margin = gap * 1.5
      const pipW = canvas.w * (landscape ? 0.2 : 0.34)
      const pipH = pipW / CAMERA_ASPECT
      const pips = rest.map((s, i) => {
        const x = canvas.w - margin - (i + 1) * pipW - i * gap
        return place(s, rect(Math.max(margin, x), canvas.h - margin - pipH, pipW, pipH), 1)
      })
      return [main, ...pips]
    }
  }
}

/** Where a frame of `src` size lands inside `target`, for cover (crop) or contain (letterbox). */
export function fitRect(src: { w: number; h: number }, target: Rect, fit: Fit): { draw: Rect; crop: Rect } {
  const scale = fit === 'cover' ? Math.max(target.w / src.w, target.h / src.h) : Math.min(target.w / src.w, target.h / src.h)
  const w = src.w * scale
  const h = src.h * scale
  if (fit === 'contain') {
    return {
      draw: { x: target.x + (target.w - w) / 2, y: target.y + (target.h - h) / 2, w, h },
      crop: { x: 0, y: 0, w: src.w, h: src.h },
    }
  }
  const cropW = target.w / scale
  const cropH = target.h / scale
  return {
    draw: target,
    crop: { x: (src.w - cropW) / 2, y: (src.h - cropH) / 2, w: cropW, h: cropH },
  }
}
